import { cn } from '@lib/cn';

import type { ReactNode } from 'react';

/* ------------------------------------------------------------------ Badge - */

export type BadgeTone =
    | 'ga'
    | 'active'
    | 'success'
    | 'beta'
    | 'trial'
    | 'warning'
    | 'dev'
    | 'info'
    | 'deprecated'
    | 'suspended'
    | 'neutral'
    | 'danger'
    | 'brand';

const TONE_CLASSES: Record<BadgeTone, { badge: string; dot: string }> = {
    ga: { badge: 'bg-emerald-50 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500' },
    active: { badge: 'bg-emerald-50 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500' },
    success: { badge: 'bg-emerald-50 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500' },
    beta: { badge: 'bg-amber-50 text-amber-700 border-amber-200', dot: 'bg-amber-500' },
    trial: { badge: 'bg-amber-50 text-amber-700 border-amber-200', dot: 'bg-amber-500' },
    warning: { badge: 'bg-amber-50 text-amber-700 border-amber-200', dot: 'bg-amber-500' },
    dev: { badge: 'bg-blue-50 text-blue-700 border-blue-200', dot: 'bg-blue-500' },
    info: { badge: 'bg-blue-50 text-blue-700 border-blue-200', dot: 'bg-blue-500' },
    deprecated: { badge: 'bg-gray-100 text-gray-600 border-gray-200', dot: 'bg-gray-400' },
    suspended: { badge: 'bg-gray-100 text-gray-600 border-gray-200', dot: 'bg-gray-400' },
    neutral: { badge: 'bg-gray-100 text-gray-600 border-gray-200', dot: 'bg-gray-400' },
    danger: { badge: 'bg-red-50 text-red-700 border-red-200', dot: 'bg-red-500' },
    brand: { badge: 'bg-brand-50 text-brand-700 border-brand-200', dot: 'bg-brand' },
};

export function Badge({
    tone = 'neutral',
    dot = false,
    children,
    className,
}: {
    tone?: BadgeTone;
    dot?: boolean;
    children: ReactNode;
    className?: string;
}) {
    const t = TONE_CLASSES[tone];
    return (
        <span
            className={cn(
                'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-0.5 text-[11px] font-semibold',
                t.badge,
                className,
            )}
        >
            {dot && <span className={cn('size-1.5 rounded-full', t.dot)} />}
            {children}
        </span>
    );
}

/* ------------------------------------------------------------- FlagKeyPill - */

export function FlagKeyPill({ children }: { children: ReactNode }) {
    return (
        <span className="inline-block rounded-md bg-gray-100 px-1.5 py-0.5 font-mono text-[12.5px] text-gray-800">
            {children}
        </span>
    );
}

/* ------------------------------------------------------------------- Card - */

export function Card({ children, className }: { children: ReactNode; className?: string }) {
    return (
        <div
            className={cn(
                'overflow-hidden rounded-xl border border-gray-200 bg-white shadow-card',
                className,
            )}
        >
            {children}
        </div>
    );
}

export function CardHeader({
    title,
    subtitle,
    action,
}: {
    title: ReactNode;
    subtitle?: ReactNode;
    action?: ReactNode;
}) {
    return (
        <div className="flex items-center gap-3 border-b border-gray-100 px-5 py-3.5">
            <h3 className="text-[15px] font-semibold text-gray-900">{title}</h3>
            {subtitle && <span className="text-[12.5px] text-gray-500">{subtitle}</span>}
            {action && <div className="ml-auto">{action}</div>}
        </div>
    );
}

/* ------------------------------------------------------------------- Chip - */

/**
 * Renders as a `<button>` when given an `onClick` and a `<span>` otherwise.
 *
 * The filter chips are now real controls, but several chips are still pure
 * labels ("Overridden 3"). Emitting a button for those would put unlabelled,
 * focusable, do-nothing stops in the keyboard order of every screen that shows
 * a count.
 */
export function Chip({
    active = false,
    onClick,
    children,
    className,
}: {
    active?: boolean;
    onClick?: () => void;
    children: ReactNode;
    className?: string;
}) {
    const classes = cn(
        'inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-[12.5px] font-medium transition-colors',
        active
            ? 'border-brand-200 bg-brand-50 text-brand-700'
            : 'border-gray-200 bg-white text-gray-600',
        onClick && !active && 'hover:bg-gray-50',
        className,
    );

    if (!onClick) return <span className={classes}>{children}</span>;

    return (
        <button type="button" onClick={onClick} aria-pressed={active} className={classes}>
            {children}
        </button>
    );
}

/* ----------------------------------------------------------------- Button - */

/**
 * `onClick`, `type` and `disabled` are passed through rather than the whole of
 * `ButtonHTMLAttributes`: the point of this component is that every button in
 * the app looks the same, and spreading arbitrary props would let a call site
 * pass its own `className` twice or override the styling wholesale.
 *
 * `disabled` is styled, not just set. An in-flight write disables its button,
 * and a control that looks live but ignores clicks reads as a broken app.
 */
export function Button({
    variant = 'primary',
    size = 'md',
    type = 'button',
    onClick,
    disabled = false,
    children,
    className,
}: {
    variant?: 'primary' | 'ghost' | 'danger';
    size?: 'md' | 'sm';
    type?: 'button' | 'submit';
    onClick?: () => void;
    disabled?: boolean;
    children: ReactNode;
    className?: string;
}) {
    // `enabled:hover:` rather than `hover:` cancelled by a conditional class:
    // `cn` is a plain joiner, so two competing `hover:bg-*` utilities would be
    // decided by stylesheet order, not by which one this component listed last.
    const VARIANT_CLASSES = {
        primary: 'border-transparent bg-brand text-white shadow-sm enabled:hover:bg-brand-600',
        ghost: 'border-gray-200 bg-white text-gray-700 enabled:hover:bg-gray-50',
        danger: 'border-red-200 bg-white text-red-700 enabled:hover:bg-red-50',
    } as const;

    return (
        <button
            type={type === 'submit' ? 'submit' : 'button'}
            onClick={onClick}
            disabled={disabled}
            className={cn(
                'inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg border font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50',
                size === 'sm' ? 'px-3 py-1.5 text-[12.5px]' : 'px-4 py-2 text-[13.5px]',
                VARIANT_CLASSES[variant],
                className,
            )}
        >
            {children}
        </button>
    );
}

/* --------------------------------------------------------------- StatTile - */

export function StatTile({
    icon,
    iconClassName,
    label,
    value,
    hint,
    hintTone = 'flat',
}: {
    icon?: ReactNode;
    iconClassName?: string;
    label: string;
    value: ReactNode;
    hint?: ReactNode;
    hintTone?: 'up' | 'flat';
}) {
    return (
        <div className="rounded-xl border border-gray-200 bg-white p-[18px] shadow-card">
            <div className="flex items-center gap-2 text-xs font-medium text-gray-500">
                {icon && (
                    <span
                        className={cn(
                            'flex size-7 items-center justify-center rounded-lg',
                            iconClassName ?? 'bg-gray-100 text-gray-500',
                        )}
                    >
                        {icon}
                    </span>
                )}
                {label}
            </div>
            <div className="mt-2 text-[28px] font-semibold tracking-tight text-gray-900">
                {value}
            </div>
            {hint && (
                <div
                    className={cn(
                        'mt-1.5 text-xs font-semibold',
                        hintTone === 'up' ? 'text-emerald-600' : 'text-gray-400',
                    )}
                >
                    {hint}
                </div>
            )}
        </div>
    );
}

/* ------------------------------------------------------------- PageHeader - */

export function PageHeader({
    eyebrow,
    title,
    description,
    actions,
}: {
    eyebrow?: string;
    title: ReactNode;
    description?: ReactNode;
    actions?: ReactNode;
}) {
    return (
        <div className="mb-6 flex items-start gap-4">
            <div>
                {eyebrow && (
                    <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-brand-600">
                        {eyebrow}
                    </div>
                )}
                <h1 className="text-2xl font-semibold tracking-tight text-gray-900">{title}</h1>
                {description && (
                    <p className="mt-1 max-w-2xl text-[13.5px] text-gray-500">{description}</p>
                )}
            </div>
            {actions && <div className="ml-auto flex shrink-0 gap-2.5">{actions}</div>}
        </div>
    );
}
